import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useDebug } from '../contexts/DebugContext';
import { fmtMoney } from './BudgetView';
import { PageHeader, Banner, DebugPanel } from './ui';

const DEFAULT_MONTHS = 6;
const monthName = (startDate) => {
  const m = /^(\d{4})-(\d{2})-/.exec(startDate || '');
  if (!m) return '';
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m[2], 10) - 1]} ${m[1]}`;
};

// A line is "zero" when every budget month value, the window total, and the
// actual invoiced amount are all zero.
const isZeroLine = (l) => {
  const monthsZero = (l.months || []).every((m) => Math.round(Number(m.amount) || 0) === 0);
  const q = Math.round(Number(l.quarterTotal) || 0);
  const a = Math.round(Number(l.actualInvoiced) || 0);
  return monthsZero && q === 0 && a === 0;
};

const cellStyle = { padding: '6px 8px', fontSize: '12px', borderBottom: '1px solid var(--gray-100)' };
const headStyle = { padding: '6px 8px', fontSize: '11px', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid var(--gray-300)', color: 'var(--gray-600)' };
const sectionRowStyle = { padding: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--gray-700, #374151)', background: 'var(--gray-50, #f8fafc)', borderTop: '2px solid var(--gray-300)', borderBottom: '1px solid var(--gray-200)' };
// Sticky header/totals so they stay visible while scrolling long budgets.
const HEADER_H = 29;
const stickyHead = { ...headStyle, position: 'sticky', top: 0, zIndex: 3, background: '#fff' };
const stickyTotalsCell = { ...cellStyle, position: 'sticky', top: `${HEADER_H}px`, zIndex: 2, background: 'var(--gray-50, #f8fafc)', fontWeight: 700 };
const scrollWrap = { overflow: 'auto', maxHeight: 'calc(100vh - 230px)' };

function lineName(l) {
  if (l.budgetLineType === 'job') {
    return (
      <>
        <div style={{ fontWeight: 500 }}>{l.jobNo}/{l.jobTaskNo} — {l.jobName}</div>
        <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{l.taskName}{l.customerName ? ` · ${l.customerName}` : ''}</div>
      </>
    );
  }
  if (l.budgetLineType === 'opportunity') {
    return (
      <>
        <div style={{ fontWeight: 500 }}>{l.opportunityName}</div>
        <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{l.opportunityNo}</div>
      </>
    );
  }
  return <div>{l.description || <span style={{ color: 'var(--gray-400)' }}>—</span>}</div>;
}

export default function BudgetAdminView() {
  const { debugMode } = useDebug();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [lines, setLines] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [actualModified, setActualModified] = useState(null);
  const [grouped, setGrouped] = useState(true);
  const [hideZero, setHideZero] = useState(false);
  const [hideNonJobsAndGoal, setHideNonJobsAndGoal] = useState(true);
  const [navDebug, setNavDebug] = useState([]);
  const [adminLocked, setAdminLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const apply = (data) => {
    setLines(data.lines || []);
    setDirectors(data.directors || []);
    setActualModified(data.actualInvoiceModified || null);
    if (data.adminLocked !== undefined) setAdminLocked(!!data.adminLocked);
    if (data.hideNonJobsAndGoal !== undefined) setHideNonJobsAndGoal(!!data.hideNonJobsAndGoal);
  };

  const doToggleLock = async () => {
    setBusy(true); setError(null);
    try {
      const r = adminLocked ? await api.unlockBudgetPeriod(selectedPeriod) : await api.lockBudgetPeriod(selectedPeriod);
      setAdminLocked(!!r.adminLocked);
      // reflect the new lock state in the periods list too
      setPeriods((prev) => prev.map((p) => (p.period === selectedPeriod ? { ...p, locked: !!r.adminLocked } : p)));
    } catch (e) { setError(e.message || 'Failed to change period lock'); }
    setBusy(false);
  };

  const load = useCallback(async (period) => {
    if (!period) { setLoading(false); return; }
    setLoading(true); setError(null);
    try { apply(await api.getBudgetAdmin(period)); }
    catch (e) { setError(e.message || 'Failed to load budgets'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.getBudgetAdminPeriods();
        const p = resp.periods;
        setPeriods(p || []);
        if (resp.hideNonJobsAndGoal !== undefined) setHideNonJobsAndGoal(!!resp.hideNonJobsAndGoal);
        // Periods run oldest→newest; default to the newest submitted quarter.
        const initial = (p && p.length) ? p[p.length - 1].period : '';
        setSelectedPeriod(initial);
        if (initial) await load(initial); else setLoading(false);
      } catch (e) { setError(e.message || 'Failed to load periods'); setLoading(false); }
    })();
  }, [load]);

  const collectNavCalls = (data) => {
    const calls = [];
    const add = (arr) => { if (Array.isArray(arr)) arr.forEach((x) => { if (x && x.request) calls.push(x); }); };
    add(data && data._navPush);
    add(data && data._debug);
    return calls;
  };

  const doLoadActuals = async () => {
    setBusy(true); setError(null); setNavDebug([]);
    try { const data = await api.loadBudgetAdminActuals(selectedPeriod, debugMode); apply(data); setNavDebug(collectNavCalls(data)); }
    catch (e) { setError(e.message || 'Failed to load actual invoiced amounts'); }
    setBusy(false);
  };
  const doExport = () => { window.open(api.getBudgetAdminExportUrl(selectedPeriod), '_blank'); };

  // Move to the previous (-1) or next (+1) quarter in the loaded periods list.
  const goQuarter = (delta) => {
    const idx = periods.findIndex((p) => p.period === selectedPeriod);
    const next = periods[idx + delta];
    if (next) { setSelectedPeriod(next.period); load(next.period); }
  };

  // Only job lines when the "Hide Non-Jobs and Goal" setting is on.
  const visibleLines = hideNonJobsAndGoal ? lines.filter((l) => l.budgetLineType === 'job') : lines;
  const N = (visibleLines[0] && visibleLines[0].months ? visibleLines[0].months.length : 0)
    || (lines[0] && lines[0].months ? lines[0].months.length : DEFAULT_MONTHS);
  const mLabels = (visibleLines[0] || lines[0])
    ? (visibleLines[0] || lines[0]).months.map((x) => monthName(x.startDate))
    : Array.from({ length: DEFAULT_MONTHS }, (_, i) => `Month ${i + 1}`);

  // Totals for a set of lines (non-submitted lines contribute 0 budget)
  const computeTotals = (subset) => {
    const t = { m: Array.from({ length: N }, () => 0), q: 0, actual: 0 };
    for (const l of subset) {
      if (l.submitted) {
        for (let k = 0; k < N; k++) t.m[k] += (l.months[k]?.amount || 0);
        t.q += Math.round(Number(l.quarterTotal) || 0);
      }
      t.actual += Math.round(Number(l.actualInvoiced) || 0);
    }
    t.variance = t.actual - t.q;
    return t;
  };
  const totals = computeTotals(visibleLines);

  const renderMonthsAndTotals = (l) => {
    if (!l.submitted) {
      return (
        <>
          <td style={{ ...cellStyle, textAlign: 'center', color: 'var(--gray-500)', fontStyle: 'italic' }} colSpan={N}>No Projections Submitted</td>
          <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(0)}</td>
        </>
      );
    }
    return (
      <>
        {Array.from({ length: N }, (_, k) => (
          <td key={k} style={{ ...cellStyle, textAlign: 'right' }}>{fmtMoney(l.months[k]?.amount)}</td>
        ))}
        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(l.quarterTotal)}</td>
      </>
    );
  };
  const renderActualVariance = (l) => {
    const variance = (Number(l.actualInvoiced) || 0) - (l.submitted ? Math.round(Number(l.quarterTotal) || 0) : 0);
    return (
      <>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{l.actualInvoiced != null ? fmtMoney(l.actualInvoiced) : <span style={{ color: 'var(--gray-300)' }}>—</span>}</td>
        <td style={{ ...cellStyle, textAlign: 'right', color: variance < 0 ? '#dc2626' : undefined }}>{fmtMoney(variance)}</td>
      </>
    );
  };

  if (loading) return <div className="loading"><div className="spinner" />Loading budgets...</div>;

  const curIndex = periods.findIndex((p) => p.period === selectedPeriod);
  const curMeta = curIndex >= 0 ? periods[curIndex] : null;
  const groupedCols = N + 4;   // Name + N months + total + actual + variance
  const flatCols = N + 5;      // + Director

  return (
    <div className="wide-section" style={{ padding: '16px' }}>
      <PageHeader
        title="Projections Admin"
        subtitle={curMeta ? curMeta.label : (periods.length === 0 ? 'No submitted projections' : selectedPeriod)}
        onPrev={curIndex > 0 ? () => goQuarter(-1) : null}
        onNext={curIndex >= 0 && curIndex < periods.length - 1 ? () => goQuarter(1) : null}
        prevTitle="Previous quarter"
        nextTitle="Next quarter"
        actions={(
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', margin: '0 6px 0 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />
              Group by director
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', margin: '0 6px 0 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
              Hide zero lines
            </label>
            {selectedPeriod && (
              <>
                <button className="btn btn-sm btn-primary" disabled={busy} onClick={doLoadActuals}>{busy ? 'Working…' : 'Load Actual Invoiced Amounts'}</button>
                <button className="btn btn-sm btn-outline" onClick={doExport}>Download to Excel</button>
                <button
                  className={`btn btn-sm ${adminLocked ? 'btn-outline' : 'btn-danger'}`}
                  disabled={busy}
                  onClick={doToggleLock}
                  title={adminLocked ? 'Allow directors to unsubmit this quarter' : 'Prevent directors from unsubmitting this quarter'}
                >
                  {adminLocked ? 'Unlock Period' : 'Lock Period'}
                </button>
              </>
            )}
          </>
        )}
      />

      {selectedPeriod && adminLocked && (
        <Banner kind="warn">This quarter is locked. Directors cannot unsubmit their projections until it is unlocked.</Banner>
      )}

      {selectedPeriod && (
        <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '8px' }}>
          Actuals last loaded: {actualModified ? new Date(actualModified).toLocaleString() : 'never'}
        </div>
      )}
      <Banner kind="err">{error}</Banner>

      <DebugPanel calls={navDebug} />

      {!selectedPeriod ? (
        <div className="card"><div style={{ padding: '16px', color: 'var(--gray-500)' }}>No quarters have submitted projections yet.</div></div>
      ) : (
        <div className="card">
          <div style={scrollWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {!grouped && <th style={stickyHead}>Director</th>}
                  <th style={stickyHead}>Name / Description</th>
                  {mLabels.map((lbl, k) => (
                    <th key={k} style={{ ...stickyHead, textAlign: 'right' }}>{lbl}</th>
                  ))}
                  <th style={{ ...stickyHead, textAlign: 'right' }}>6 mos. (Projected)</th>
                  <th style={{ ...stickyHead, textAlign: 'right' }}>Actual Invoiced</th>
                  <th style={{ ...stickyHead, textAlign: 'right' }}>Variance</th>
                </tr>
                <tr>
                  {!grouped && <td style={stickyTotalsCell}></td>}
                  <td style={stickyTotalsCell}>Totals (all directors)</td>
                  {totals.m.map((mv, k) => (
                    <td key={k} style={{ ...stickyTotalsCell, textAlign: 'right' }}>{fmtMoney(mv)}</td>
                  ))}
                  <td style={{ ...stickyTotalsCell, textAlign: 'right' }}>{fmtMoney(totals.q)}</td>
                  <td style={{ ...stickyTotalsCell, textAlign: 'right' }}>{fmtMoney(totals.actual)}</td>
                  <td style={{ ...stickyTotalsCell, textAlign: 'right', color: totals.variance < 0 ? '#dc2626' : undefined }}>{fmtMoney(totals.variance)}</td>
                </tr>
              </thead>
              <tbody>
                {visibleLines.length === 0 && (
                  <tr><td style={cellStyle} colSpan={grouped ? groupedCols : flatCols}><span style={{ color: 'var(--gray-400)' }}>No budgets for this quarter.</span></td></tr>
                )}
                {grouped ? (
                  directors.map((d) => {
                    const dLines = visibleLines.filter((l) => l.directorResourceNo === d.resourceNo && (!hideZero || !isZeroLine(l)));
                    if (hideZero && dLines.length === 0) return null;
                    const dt = computeTotals(visibleLines.filter((l) => l.directorResourceNo === d.resourceNo));
                    return (
                    <React.Fragment key={d.resourceNo || 'none'}>
                      <tr>
                        <td style={sectionRowStyle}>
                          {d.name || d.resourceNo}
                          {!d.submitted && <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: '#b45309' }}>(No Projections Submitted)</span>}
                        </td>
                        {dt.m.map((mv, k) => (
                          <td key={k} style={{ ...sectionRowStyle, textAlign: 'right' }}>{fmtMoney(mv)}</td>
                        ))}
                        <td style={{ ...sectionRowStyle, textAlign: 'right' }}>{fmtMoney(dt.q)}</td>
                        <td style={{ ...sectionRowStyle, textAlign: 'right' }}>{fmtMoney(dt.actual)}</td>
                        <td style={{ ...sectionRowStyle, textAlign: 'right', color: dt.variance < 0 ? '#dc2626' : undefined }}>{fmtMoney(dt.variance)}</td>
                      </tr>
                      {dLines.map((l) => (
                        <tr key={l.lineGroupId}>
                          <td style={cellStyle}>{lineName(l)}</td>
                          {renderMonthsAndTotals(l)}
                          {renderActualVariance(l)}
                        </tr>
                      ))}
                    </React.Fragment>
                    );
                  })
                ) : (
                  (hideZero ? visibleLines.filter((l) => !isZeroLine(l)) : visibleLines).map((l) => (
                    <tr key={l.lineGroupId}>
                      <td style={cellStyle}>{l.directorResourceNo}</td>
                      <td style={cellStyle}>{lineName(l)}</td>
                      {renderMonthsAndTotals(l)}
                      {renderActualVariance(l)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
